import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Upload, Save, Calendar as CalendarIcon, Flag, Users, X, FileText } from "lucide-react";
import { toast } from "sonner";
import Gantt from "frappe-gantt";
import "./frappe-gantt.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_TASK = {
  stage_name: "",
  substage_name: "",
  task_name: "",
  team_id: "",
  is_milestone: false,
  percent_complete: 0,
  start_date: "",
  end_date: "",
  duration_days: "",
  notes: "",
  order_index: 0,
};

export default function ProjectSchedule({ companyId, projectId, projectName }) {
  const [tasks, setTasks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [suggestedStages, setSuggestedStages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog de Tarefa
  const [taskDialog, setTaskDialog] = useState({ open: false, task: { ...EMPTY_TASK } });
  // Dialog de Equipes
  const [teamsDialog, setTeamsDialog] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: "", contact: "", role: "" });

  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);
  const [ganttMode, setGanttMode] = useState("Week");

  const fetchAll = async () => {
    if (!companyId || !projectId) return;
    setLoading(true);
    try {
      const [tasksRes, teamsRes, stagesRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}/projects/${projectId}/schedule`),
        axios.get(`${API}/companies/${companyId}/construction-teams`),
        axios.get(`${API}/schedule/suggested-stages`),
      ]);
      setTasks(tasksRes.data || []);
      setTeams(teamsRes.data || []);
      setSuggestedStages(stagesRes.data?.stages || []);
    } catch (e) {
      toast.error("Erro ao carregar cronograma");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [companyId, projectId]);

  // Renderiza Gantt
  useEffect(() => {
    if (!ganttRef.current) return;
    // Blindagem: ignora datas fora de um range plausível (2000-2100) para não travar o Gantt
    const isValidDate = (s) => {
      if (!s || typeof s !== "string") return false;
      const d = new Date(s);
      if (isNaN(d.getTime())) return false;
      const y = d.getFullYear();
      return y >= 2000 && y <= 2100;
    };
    const ganttTasks = tasks
      .filter(t => isValidDate(t.start_date) && isValidDate(t.end_date))
      .map(t => ({
        id: t.id,
        name: (t.is_milestone ? "★ " : "") + t.task_name,
        start: t.start_date,
        end: t.end_date,
        progress: t.percent_complete || 0,
        custom_class: t.is_milestone ? "gantt-milestone" : "",
      }));
    ganttRef.current.textContent = "";
    if (ganttTasks.length === 0) {
      const empty = document.createElement("div");
      empty.className = "text-center py-12 text-gray-500";
      empty.textContent = "Adicione tarefas com datas para visualizar o Gantt";
      ganttRef.current.appendChild(empty);
      return;
    }
    try {
      ganttInstance.current = new Gantt(ganttRef.current, ganttTasks, {
        view_mode: ganttMode,
        date_format: "YYYY-MM-DD",
        language: "pt-BR",
        bar_height: 20,
        padding: 18,
      });
    } catch (e) {
      console.error("Gantt error:", e);
    }
  }, [tasks, ganttMode]);

  const totalProgress = useMemo(() => {
    if (tasks.length === 0) return 0;
    const sum = tasks.reduce((s, t) => s + (parseFloat(t.percent_complete) || 0), 0);
    return Math.round(sum / tasks.length);
  }, [tasks]);

  const tasksByStage = useMemo(() => {
    const groups = {};
    tasks.forEach(t => {
      const stage = t.stage_name || "Sem etapa";
      if (!groups[stage]) groups[stage] = [];
      groups[stage].push(t);
    });
    return groups;
  }, [tasks]);

  const openNewTask = (preStage = "") => {
    setTaskDialog({ open: true, task: { ...EMPTY_TASK, stage_name: preStage, order_index: tasks.length } });
  };

  const openEditTask = (t) => {
    setTaskDialog({
      open: true,
      task: {
        ...EMPTY_TASK,
        ...t,
        start_date: t.start_date || "",
        end_date: t.end_date || "",
        duration_days: t.duration_days || "",
        team_id: t.team_id || "",
      },
    });
  };

  const saveTask = async () => {
    const t = taskDialog.task;
    if (!t.stage_name?.trim() || !t.task_name?.trim()) {
      toast.error("Etapa e Nome da Tarefa são obrigatórios");
      return;
    }
    const payload = {
      stage_name: t.stage_name,
      substage_name: t.substage_name || null,
      task_name: t.task_name,
      team_id: t.team_id || null,
      is_milestone: !!t.is_milestone,
      percent_complete: parseFloat(t.percent_complete) || 0,
      start_date: t.start_date || null,
      end_date: t.end_date || null,
      duration_days: t.duration_days ? parseInt(t.duration_days) : null,
      notes: t.notes || null,
      order_index: parseInt(t.order_index) || 0,
    };
    try {
      if (t.id) {
        await axios.put(`${API}/companies/${companyId}/projects/${projectId}/schedule/${t.id}`, payload);
        toast.success("Tarefa atualizada");
      } else {
        await axios.post(`${API}/companies/${companyId}/projects/${projectId}/schedule`, payload);
        toast.success("Tarefa criada");
      }
      setTaskDialog({ open: false, task: { ...EMPTY_TASK } });
      fetchAll();
    } catch (e) {
      toast.error("Erro ao salvar: " + (e.response?.data?.detail || ""));
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Excluir esta tarefa?")) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/projects/${projectId}/schedule/${id}`);
      toast.success("Tarefa excluída");
      fetchAll();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const toggleCompleted = async (task, checked) => {
    try {
      await axios.patch(`${API}/companies/${companyId}/projects/${projectId}/schedule/${task.id}/status`, {
        completed: checked,
      });
      toast.success(checked ? "Tarefa marcada como realizada" : "Tarefa desmarcada");
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const setDeadlineMet = async (task, met) => {
    try {
      await axios.patch(`${API}/companies/${companyId}/projects/${projectId}/schedule/${task.id}/status`, {
        deadline_met: met,
      });
      toast.success(met ? "Prazo marcado como cumprido" : "Prazo marcado como atrasado");
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };


  const saveTeam = async () => {
    if (!newTeam.name?.trim()) {
      toast.error("Nome da equipe obrigatório");
      return;
    }
    try {
      await axios.post(`${API}/companies/${companyId}/construction-teams`, newTeam);
      toast.success("Equipe cadastrada");
      setNewTeam({ name: "", contact: "", role: "" });
      const r = await axios.get(`${API}/companies/${companyId}/construction-teams`);
      setTeams(r.data || []);
    } catch (e) {
      toast.error("Erro ao salvar equipe");
    }
  };

  const deleteTeam = async (id) => {
    if (!window.confirm("Excluir esta equipe?")) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/construction-teams/${id}`);
      const r = await axios.get(`${API}/companies/${companyId}/construction-teams`);
      setTeams(r.data || []);
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const uploadXlsx = async (file) => {
    if (!file) return;
    if (!window.confirm(`Importar tarefas do arquivo "${file.name}"? As tarefas serão adicionadas ao cronograma atual.`)) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/projects/${projectId}/schedule/import-xlsx`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${res.data.imported} tarefa(s) importada(s)`);
      fetchAll();
    } catch (e) {
      toast.error("Erro ao importar: " + (e.response?.data?.detail || ""));
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (!window.confirm(`Importar tarefas do PDF "${file.name}"? As tarefas serão adicionadas ao cronograma atual.`)) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/projects/${projectId}/schedule/import-pdf`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const n = res.data.imported || 0;
      if (n === 0) {
        toast.error("Nenhuma tarefa reconhecida. Verifique se o PDF não é escaneado (imagem).");
      } else {
        toast.success(`${n} tarefa(s) importada(s)${res.data.section_found ? " (seção Serviços componentes localizada)" : " (analisando todo o PDF)"}`);
      }
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // Se detail vier com preview de linhas do PDF, mostra em alert (mais legível)
      if (detail.includes("Primeiras linhas")) {
        window.alert("Não consegui reconhecer serviços neste PDF.\n\n" + detail);
      } else {
        toast.error("Erro ao importar: " + detail);
      }
    }
  };

  const sanitizeSchedule = async () => {
    if (!window.confirm("Limpar datas inválidas (fora do intervalo 2000-2100) e durações absurdas das tarefas existentes? As tarefas serão preservadas, apenas as datas quebradas serão removidas.")) return;
    try {
      const res = await axios.post(`${API}/companies/${companyId}/projects/${projectId}/schedule/sanitize`);
      toast.success(`${res.data.fixed || 0} tarefa(s) corrigida(s)`);
      fetchAll();
    } catch (e) {
      toast.error("Erro ao sanitizar: " + (e.response?.data?.detail || ""));
    }
  };

  const clearSchedule = async () => {
    if (!window.confirm("ATENÇÃO: Isso vai apagar TODAS as tarefas do cronograma deste projeto. Confirmar?")) return;
    if (!window.confirm("Tem absoluta certeza? Esta ação não pode ser desfeita.")) return;
    try {
      const res = await axios.delete(`${API}/companies/${companyId}/projects/${projectId}/schedule`);
      toast.success(`${res.data.deleted || 0} tarefa(s) removida(s)`);
      fetchAll();
    } catch (e) {
      toast.error("Erro ao limpar: " + (e.response?.data?.detail || ""));
    }
  };

  return (
    <div className="space-y-3 project-schedule-tab min-w-0 w-full max-w-full overflow-hidden" data-testid="project-schedule">
      <style>{`
        .project-schedule-tab, .project-schedule-tab * { color: #000000 !important; }
        .project-schedule-tab label, .project-schedule-tab th { font-weight: 700 !important; }
        .project-schedule-tab .badge-progress { color: #ffffff !important; }
        .project-schedule-tab input::placeholder, .project-schedule-tab textarea::placeholder {
          color: #4b5563 !important; opacity: 1 !important;
        }
        /* Densidade para caber na tela do notebook (1366-1440px) */
        .project-schedule-tab table td, .project-schedule-tab table th {
          padding: 6px 8px !important;
          font-size: 12px !important;
        }
        /* Impede que o Gantt/tabela empurre o layout para fora da viewport */
        .project-schedule-tab .gantt-container {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
        }
        .project-schedule-tab .gantt-container svg {
          display: block;
        }
        .project-schedule-tab .stage-table-wrapper {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
        }
        .project-schedule-tab .card,
        .project-schedule-tab .rounded-lg {
          min-width: 0;
        }
      `}</style>
      {/* Header — compacto */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h3 className="text-lg md:text-xl font-extrabold flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Cronograma Físico de Obra
          </h3>
          <p className="text-xs">Projeto: {projectName || "—"}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setTeamsDialog(true)} data-testid="btn-manage-teams">
            <Users className="h-3.5 w-3.5 mr-1" /> Equipes ({teams.length})
          </Button>
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadXlsx(f);
                e.target.value = "";
              }}
              data-testid="schedule-xlsx-upload"
            />
            <span className="inline-flex items-center px-2.5 py-1.5 text-xs border rounded-md bg-white hover:bg-gray-50">
              <Upload className="h-3.5 w-3.5 mr-1" /> Importar XLSX
            </span>
          </label>
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadPdf(f);
                e.target.value = "";
              }}
              data-testid="schedule-pdf-upload"
            />
            <span className="inline-flex items-center px-2.5 py-1.5 text-xs border rounded-md bg-white hover:bg-gray-50">
              <FileText className="h-3.5 w-3.5 mr-1" /> Importar PDF
            </span>
          </label>
          <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={sanitizeSchedule} data-testid="btn-sanitize-schedule">
            Limpar Datas Inválidas
          </Button>
          <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={clearSchedule} data-testid="btn-clear-schedule">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Zerar Cronograma
          </Button>
          <Button size="sm" className="bg-blue-500 hover:bg-blue-600" onClick={() => openNewTask()} data-testid="btn-new-task">
            <Plus className="h-3.5 w-3.5 mr-1" /> Nova Tarefa
          </Button>
        </div>
      </div>

      {/* Resumo — compacto */}
      <Card>
        <CardContent className="p-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-[11px]">Tarefas</p>
              <p className="text-lg md:text-xl font-extrabold">{tasks.length}</p>
            </div>
            <div>
              <p className="text-[11px]">Marcos</p>
              <p className="text-lg md:text-xl font-extrabold" style={{ color: "#ca8a04" }}>{tasks.filter(t => t.is_milestone).length}</p>
            </div>
            <div>
              <p className="text-[11px]">Progresso Médio</p>
              <div className="flex items-center gap-2">
                <p className="text-lg md:text-xl font-extrabold" style={{ color: "#16a34a" }}>{totalProgress}%</p>
                <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="bg-green-500 h-full" style={{ width: `${totalProgress}%` }} />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gantt */}
      <Card>
        <CardHeader className="pb-1 p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm md:text-base">Visualização Gantt</CardTitle>
            <div className="flex gap-1">
              {["Day", "Week", "Month"].map(m => (
                <Button
                  key={m}
                  size="sm"
                  variant={ganttMode === m ? "default" : "outline"}
                  onClick={() => setGanttMode(m)}
                  data-testid={`gantt-mode-${m.toLowerCase()}`}
                >
                  {m === "Day" ? "Dia" : m === "Week" ? "Semana" : "Mês"}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-2 md:p-3">
          <div className="gantt-container">
            <svg ref={ganttRef} />
          </div>
        </CardContent>
      </Card>

      {/* Tabela por Etapa */}
      <Card>
        <CardHeader className="pb-1 p-3">
          <CardTitle className="text-sm md:text-base">Tarefas por Etapa</CardTitle>
        </CardHeader>
        <CardContent className="p-2 md:p-3">
          {loading ? (
            <p className="text-center py-3">Carregando...</p>
          ) : tasks.length === 0 ? (
            <div className="text-center py-6">
              <CalendarIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>Nenhuma tarefa cadastrada</p>
              <p className="text-xs mt-1">Clique em &quot;Nova Tarefa&quot;, &quot;Importar XLSX&quot; ou &quot;Importar PDF&quot; para começar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(tasksByStage).map(([stage, stageTasks]) => (
                <div key={stage} className="border rounded-lg overflow-hidden min-w-0">
                  <div className="bg-blue-50 px-3 py-2 flex items-center justify-between">
                    <h4 className="font-bold text-blue-900 text-sm">{stage}</h4>
                    <Button size="sm" variant="ghost" onClick={() => openNewTask(stage)}>
                      <Plus className="h-3 w-3 mr-1" /> Tarefa
                    </Button>
                  </div>
                  <div className="stage-table-wrapper">
                    <table className="w-full text-sm min-w-[900px]">
                      <thead className="bg-gray-50 text-xs">
                        <tr>
                          <th className="text-left">Item</th>
                          <th className="text-left">Serviço</th>
                          <th className="text-center">Un</th>
                          <th className="text-right">Qtd</th>
                          <th className="text-right">Custo Ref.</th>
                          <th className="text-center">%</th>
                          <th className="text-left">Início</th>
                          <th className="text-left">Fim</th>
                          <th className="text-center">Realizado</th>
                          <th className="text-center">Prazo</th>
                          <th className="text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stageTasks.map(t => {
                          return (
                            <tr key={t.id} className={`border-t hover:bg-gray-50 ${t.completed ? "bg-green-50" : ""}`} data-testid={`task-row-${t.id}`}>
                              <td className="font-mono">{t.item_code || "—"}</td>
                              <td>
                                <div className="flex items-center gap-1">
                                  {t.is_milestone && <Flag className="h-3 w-3 text-yellow-600" />}
                                  <span className={t.is_milestone ? "font-bold" : ""}>{t.task_name}</span>
                                </div>
                                {t.substage_name && <div className="text-[10px]">{t.substage_name}</div>}
                              </td>
                              <td className="text-center">{t.unit || "—"}</td>
                              <td className="text-right">{t.quantity != null ? Number(t.quantity).toLocaleString("pt-BR") : "—"}</td>
                              <td className="text-right">{t.reference_cost != null ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(t.reference_cost) : "—"}</td>
                              <td className="text-center">
                                <Badge variant={t.percent_complete >= 100 ? "default" : "outline"}>
                                  {Math.round(t.percent_complete || 0)}%
                                </Badge>
                              </td>
                              <td>{t.start_date ? new Date(t.start_date).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—"}</td>
                              <td>{t.end_date ? new Date(t.end_date).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—"}</td>
                              <td className="text-center">
                                <label className="inline-flex items-center gap-1 cursor-pointer" data-testid={`task-completed-${t.id}`}>
                                  <input
                                    type="checkbox"
                                    checked={!!t.completed}
                                    onChange={(e) => toggleCompleted(t, e.target.checked)}
                                    className="h-4 w-4"
                                  />
                                  {t.completed && <span className="text-green-700 text-[10px] font-bold">OK</span>}
                                </label>
                              </td>
                              <td className="text-center">
                                {t.completed ? (
                                  <div className="flex gap-1 justify-center">
                                    <Button
                                      size="sm"
                                      variant={t.deadline_met === true ? "default" : "outline"}
                                      className={t.deadline_met === true ? "bg-green-500 hover:bg-green-600 h-6 px-2 text-[10px]" : "h-6 px-2 text-[10px]"}
                                      onClick={() => setDeadlineMet(t, true)}
                                      data-testid={`task-deadline-yes-${t.id}`}
                                    >
                                      Cumprido
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={t.deadline_met === false ? "default" : "outline"}
                                      className={t.deadline_met === false ? "bg-red-500 hover:bg-red-600 h-6 px-2 text-[10px]" : "h-6 px-2 text-[10px]"}
                                      onClick={() => setDeadlineMet(t, false)}
                                      data-testid={`task-deadline-no-${t.id}`}
                                    >
                                      Atrasado
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-[10px]">—</span>
                                )}
                              </td>
                              <td className="text-center">
                                <div className="flex gap-1 justify-center">
                                  <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => openEditTask(t)} data-testid={`task-edit-${t.id}`}>Editar</Button>
                                  <Button size="sm" variant="outline" className="text-red-600 h-6 px-2" onClick={() => deleteTask(t.id)}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Tarefa */}
      <Dialog open={taskDialog.open} onOpenChange={(open) => setTaskDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{taskDialog.task.id ? "Editar Tarefa" : "Nova Tarefa"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-black block mb-1">Etapa *</label>
                <Input
                  list="stage-suggestions"
                  value={taskDialog.task.stage_name}
                  onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, stage_name: e.target.value } }))}
                  className="text-black"
                  placeholder="Ex: ESTRUTURA DE CONCRETO"
                  data-testid="task-stage-input"
                />
                <datalist id="stage-suggestions">
                  {suggestedStages.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div>
                <label className="text-xs font-bold text-black block mb-1">Sub-etapa</label>
                <Input
                  value={taskDialog.task.substage_name || ""}
                  onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, substage_name: e.target.value } }))}
                  className="text-black"
                  placeholder="Ex: Pilares"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-black block mb-1">Nome da Tarefa *</label>
              <Input
                value={taskDialog.task.task_name}
                onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, task_name: e.target.value } }))}
                className="text-black"
                placeholder="Ex: Viga baldrame"
                data-testid="task-name-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-black block mb-1">Responsável (Equipe)</label>
                <Select
                  value={taskDialog.task.team_id || "none"}
                  onValueChange={(v) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, team_id: v === "none" ? "" : v } }))}
                >
                  <SelectTrigger className="text-black"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nenhum —</SelectItem>
                    {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.role ? ` (${t.role})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold text-black block mb-1">% Concluído</label>
                <Input
                  type="number" min="0" max="100" step="1"
                  value={taskDialog.task.percent_complete}
                  onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, percent_complete: e.target.value } }))}
                  className="text-black"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-black block mb-1">Início</label>
                <Input
                  type="date"
                  value={taskDialog.task.start_date || ""}
                  onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, start_date: e.target.value } }))}
                  className="text-black"
                  data-testid="task-start-date"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-black block mb-1">Fim</label>
                <Input
                  type="date"
                  value={taskDialog.task.end_date || ""}
                  onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, end_date: e.target.value } }))}
                  className="text-black"
                  data-testid="task-end-date"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-black block mb-1">Duração (dias)</label>
                <Input
                  type="number" min="1" step="1"
                  value={taskDialog.task.duration_days}
                  onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, duration_days: e.target.value } }))}
                  className="text-black"
                  placeholder="opcional"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="task-milestone"
                checked={!!taskDialog.task.is_milestone}
                onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, is_milestone: e.target.checked } }))}
                className="h-4 w-4"
                data-testid="task-milestone-check"
              />
              <label htmlFor="task-milestone" className="text-sm font-bold text-black flex items-center gap-1">
                <Flag className="h-4 w-4 text-yellow-600" /> Marco / Milestone
              </label>
            </div>
            <div>
              <label className="text-xs font-bold text-black block mb-1">Observações</label>
              <Input
                value={taskDialog.task.notes || ""}
                onChange={(e) => setTaskDialog(prev => ({ ...prev, task: { ...prev.task, notes: e.target.value } }))}
                className="text-black"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTaskDialog({ open: false, task: { ...EMPTY_TASK } })}>Cancelar</Button>
              <Button className="bg-blue-500 hover:bg-blue-600" onClick={saveTask} data-testid="task-save-btn">
                <Save className="h-4 w-4 mr-1" /> Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Equipes */}
      <Dialog open={teamsDialog} onOpenChange={setTeamsDialog}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerenciar Equipes / Responsáveis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
              <p className="text-sm font-bold text-black">Cadastrar Nova Equipe</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Nome *" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} className="text-black" data-testid="team-name-input" />
                <Input placeholder="Função (ex: Pedreiro)" value={newTeam.role} onChange={(e) => setNewTeam({ ...newTeam, role: e.target.value })} className="text-black" />
                <Input placeholder="Contato (telefone/email)" value={newTeam.contact} onChange={(e) => setNewTeam({ ...newTeam, contact: e.target.value })} className="text-black" />
              </div>
              <Button size="sm" className="bg-blue-500 hover:bg-blue-600" onClick={saveTeam} data-testid="team-save-btn">
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {teams.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nenhuma equipe cadastrada</p>
              ) : (
                teams.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div>
                      <p className="font-medium text-black">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role || "—"}{t.contact ? ` · ${t.contact}` : ""}</p>
                    </div>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTeam(t.id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
